<template>
  <div class="tab-bar" :class="{ 'is-home': activePath === '/' }">
    <div class="tab-bar-container">
      <div
        v-for="(item, index) in tabItems"
        :key="index"
        class="tab-item"
        :class="{ active: activePath === item.path }"
        @click="handleTabClick(item.path)"
      >
        <div class="tab-icon-wrapper">
          <img
            :src="activePath === item.path ? item.activeIcon : item.icon"
            :alt="t(item.nameKey)"
            class="tab-icon"
          />
          <!-- 未读消息气泡 -->
          <div
            v-if="item.path === '/notice' && userStore.totalUnreadCount > 0"
            class="unread-badge"
          >
            {{
              userStore.totalUnreadCount > 99
                ? "99+"
                : userStore.totalUnreadCount
            }}
          </div>
          <!-- 新增粉丝气泡 -->
          <div
            v-if="item.path === '/profile' && userStore.newFollowerCount > 0"
            class="unread-badge"
          >
            {{
              userStore.newFollowerCount > 99
                ? "99+"
                : userStore.newFollowerCount
            }}
          </div>
        </div>
        <span class="tab-name">{{ t(item.nameKey) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores";
import { clickButton, pageType } from "@/utils/point";
const router = useRouter();
const { t } = useI18n();
const activePath = ref(router.currentRoute.value.path);
const userStore = useUserStore();
const tabItems = [
  {
    nameKey: "tabBar.home",
    path: "/",
    icon: new URL("../assets/router-home.png", import.meta.url).href,
    activeIcon: new URL("../assets/router-home-active.png", import.meta.url)
      .href,
  },
  {
    nameKey: "tabBar.notice",
    path: "/notice",
    icon: new URL("../assets/router-notice.png", import.meta.url).href,
    activeIcon: new URL("../assets/router-notice-active.png", import.meta.url)
      .href,
  },
  {
    nameKey: "tabBar.profile",
    path: "/profile",
    icon: new URL("../assets/router-profile.png", import.meta.url).href,
    activeIcon: new URL("../assets/router-profile-active.png", import.meta.url)
      .href,
  },
];

// 监听路由变化
router.afterEach((to) => {
  activePath.value = to.path;
});

const handleTabClick = async (path: string) => {
  if (activePath.value === path) {
    return;
  }
  clickButton({
    eventType: pageType.MENU,
    eventValue: path,
  });
  if (["/profile", "/notice"].includes(path) && !userStore.checkLogin()) {
    return;
  }
  await userStore.getNotificationCount();
  activePath.value = path;
  router.push(path);
};
</script>

<style lang="scss" scoped>
$pc-content-block-half: 550px; // 与 Home.vue 一致，(800+20+280)/2，居中后内容列左缘

.tab-bar {
  position: fixed;
  bottom: 0;
  width: 100vw;
  height: 60px;
  background-color: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
}

.tab-bar-container {
  max-width: 800px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  align-items: center;
}

@media (min-width: 1024px) {
  .tab-bar.is-home {
    width: 800px;
    left: calc(50% - #{$pc-content-block-half});
    right: auto;
    margin-left: 0;
    border-radius: 15px 15px 0 0;
  }

  .tab-bar.is-home .tab-bar-container {
    margin-left: 0;
    margin-right: 0;
  }
}

.tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  cursor: pointer;
}

.tab-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 2px;
}

.tab-name {
  font-size: 12px;
  color: #666;
}

.tab-item.active .tab-name {
  color: #333;
  font-weight: 500;
}

.unread-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  background-color: #df4c6e;
  color: white;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
}
</style>
